<template>
	<div
		v-if="loading"
		class="loading-spinner-container"
	>
		<LoadingSpinner :is-overlay="true" />
	</div>
	<div v-else>
		<div class="journeys-skew-banner">
			<div class="container">
				<div class="row">
					<div class="col journeys-skew-banner__content text-center">
						<h1 class="h2">{{ microcopy?.heading }}</h1>
						<p>{{ microcopy?.summary }}</p>
					</div>
				</div>
			</div>
		</div>
		<div class="container pull-up">
			<form
				class="w-100"
				@submit.prevent
			>
				<div class="rounded-container rounded-container--narrow">
					<div class="row">
						<div class="col rounded-container__btn-col">
							<ValidationSummaryComponent v-if="someInvalid" />
							<TextInputField
								required
								:manual-input-dirtier="buttonClicked"
								:label-text="microcopy?.firstNameInputLabel"
								:error-text="microcopy?.firstNameInputError"
								@field-input="(payload) => {
									inputValue.firstName = payload.value;
									isValid.firstName = payload.isValid;
								}"
							/>
							<TextInputField
								required
								:manual-input-dirtier="buttonClicked"
								:error-text="microcopy?.surnameInputError"
								:label-text="microcopy?.surnameInputLabel"
								@field-input="(payload) => {
									inputValue.surname = payload.value;
									isValid.surname = payload.isValid;
								}"
							/>
							<DropdownField
								:options="professionalFields"
								:manual-input-dirtier="buttonClicked"
								:label-text="microcopy?.segmentDropdownLabel"
								:error-text="microcopy?.segmentDropdownError"
								@selected="(payload) => {
									inputValue.professionalField = payload;
									isValid.professionalField = payload !== undefined;
								}"
							/>
							<TextInputField
								required
								:manual-input-dirtier="buttonClicked"
								:error-text="microcopy?.emailInputError"
								:label-text="microcopy?.emailInputLabel"
								input-type="email"
								@field-input="(payload) => {
									inputValue.email = payload.value;
									isValid.email = payload.isValid;
								}"
							/>
							<TextInputField
								required
								:manual-input-dirtier="buttonClicked"
								:label-text="microcopy?.contactNumberInputLabel"
								:error-text="microcopy?.contactNumberInputError"
								input-type="phoneNumber"
								@field-input="(payload) => {
									inputValue.phoneNumber = payload.value;
									isValid.phoneNumber = payload.isValid;
								}"
							/>
							<p class="label form-label">
								{{ microcopy?.marketingPreferencesLabel }}
							</p>
							<p>{{ microcopy?.marketingPreferencesText }}</p>
							<div class="std-checkbox-row std-form-item std-form-item--sm">
								<CheckBox
									label-text="Email"
									@selected="(payload) => {
										inputValue.contactMethod.email = payload;
									}"
								/>
								<CheckBox
									label-text="Text message"
									@selected="(payload) => {
										inputValue.contactMethod.textMessage = payload;
									}"
								/>
								<CheckBox
									label-text="Phone"
									@selected="(payload) => {
										inputValue.contactMethod.phone = payload;
									}"
								/>
								<CheckBox
									label-text="Post"
									@selected="(payload) => {
										inputValue.contactMethod.post = payload;
									}"
								/>
							</div>
							<span v-html="microcopy?.disclaimer"></span>
							<GenericButton
								class="sfc-submit-btn mt-5"
								:label-text="microcopy?.submitButtonLabel"
								@button-click="handleClick"
							/>
							<p
								class="error-text"
								v-if="error"
							>{{ submissionError }}</p>
						</div>
					</div>
				</div>
			</form>
		</div>
	</div>
</template>

<script>

import DropdownField from '@/shared/components/DropdownField.vue';
import TextInputField from '@/shared/components/TextInputField.vue';
import GenericButton from '@/shared/components/GenericButton.vue';
import BackButton from '@/shared/components/BackButton.vue';
import CheckBox from '@/shared/components/CheckBox.vue';
import ValidationSummaryComponent from '../../shared/components/ValidationSummaryComponent.vue';
import LoadingSpinner from '@/shared/components/LoadingSpinner.vue';

import { scrollToInvalidField } from '@/shared/helpers';

import router from '../router/index.js';

import { mapStores, mapState } from 'pinia';
import { useMortgageStore } from '../stores/mortgageStore.js';

export default {
	components: {
		TextInputField,
		GenericButton,
		BackButton,
		DropdownField,
		CheckBox,
		ValidationSummaryComponent,
		LoadingSpinner
	},
	data() {
		return {
			microcopy: '',
			microcopyEmailSettings: '',
			professionalFields: ['Teacher', 'Hospital', 'GP', 'Dentist', 'Other'],
			isValid: {
				firstName: false,
				surname: false,
				professionalField: false,
				email: false,
				phoneNumber: false,
			},
			inputValue: {
				firstName: '',
				surname: '',
				professionalField: '',
				email: '',
				phoneNumber: '',
				contactMethod: {
					email: false,
					phone: false,
					post: false,
					textMessage: false,
					timestamp: '',
				},
			},
			allFieldsFromStore: '',
			error: false,
			submissionError: '',
			buttonClicked: false,
			loading: false
		};
	},
	computed: {
		...mapStores(useMortgageStore),
		...mapState(useMortgageStore, ['getAll']),
		someInvalid() {
			return Object.values(this.isValid).includes(false) && this.buttonClicked;
		}
	},
	mounted() {
		this.allFieldsFromStore = this.getAll;

		const el = document.querySelector('.step--yourdetails');
		if (el?.classList.contains('d-none')) {
			el?.classList.remove('d-none');
		}
		this.microcopy = window.mortgageRequestMicrocopy.yourDetails;
		this.microcopyEmailSettings = window.mortgageRequestMicrocopy.emailSettings;
		document.title = this.microcopy?.metaTitle
	},
	methods: {
		handleClick() {
			if (Object.values(this.isValid).includes(false)) {
				this.buttonClicked = true;
				setTimeout(() => {
					scrollToInvalidField()
				}, "50");
			}
			else {
				this.submitData();
			}
		},
		handleBackButton() {
			router.back();
		},
		submitData() {
			this.loading = true;

			scrollTo({ top: 0, behavior: 'smooth' });

			const myHeaders = new Headers();
			myHeaders.append("Content-Type", "application/json");
			this.inputValue.contactMethod.timestamp = new Date().toISOString();

			const raw = JSON.stringify({
				"forename": this.inputValue.firstName,
				"surname": this.inputValue.surname,
				"segment": this.inputValue.professionalField,
				"email": this.inputValue.email,
				"contactNumber": this.inputValue.phoneNumber,
				"marketingPreferences": {
					"email": this.inputValue.contactMethod.email,
					"phone": this.inputValue.contactMethod.phone,
					"post": this.inputValue.contactMethod.post,
					"timestamp": this.inputValue.contactMethod.timestamp
				},
				"enquiryType": this.allFieldsFromStore?.mortgageType.type,
				"stage": this.allFieldsFromStore?.propertySearchStage.text,
				"costOfProperty": parseInt(this.allFieldsFromStore?.propertyValue),
				"deposit": parseInt(this.allFieldsFromStore?.deposit),
				"useOfProperty": this.allFieldsFromStore?.remortgage.text,
				"mortgageDealEnding": this.allFieldsFromStore?.mortgageDealEnding.text,
				"mortgageProvider": this.allFieldsFromStore?.previousMortgageProvider.text,
				"increaseBorrowing": this.allFieldsFromStore?.increaseBorrowing.text,
				"howMuchToBorrow": this.allFieldsFromStore?.borrowingAmount,
				"businessEmail": this.microcopyEmailSettings.businessEmailSetting,
				"callbackEmail": this.microcopyEmailSettings.callbackEmailSetting,
				"customerEmail": this.microcopyEmailSettings.customerEmailSetting
			});

			appInsights.trackTrace({
				message: '[GAMJourney] API submission - Started',
				properties: { area: 'mortgage-journey', hostname: location.hostname },
				severityLevel: 1
			});

			const options = {
				method: "POST",
				headers: myHeaders,
				body: raw,
				redirect: "follow"
			};

			fetch('/sapi/Mortgages/submit', options)
				.then((res) => {
					if (!res.ok) {
						console.error(`Request failed with status ${res.status}`);
						appInsights.trackTrace({
							message: `[GAMJourney] API submission - Failed - received HTTP ${res.status}`,
							properties: { area: 'mortgage-journey', hostname: location.hostname },
							severityLevel: 4
						});

						this.error = true;

						if (res.status === 404) {
							this.submissionError = "Request timed out";
						}

						return;
					}

					appInsights.trackTrace({
						message: '[GAMJourney] API submission - Succeeded',
						properties: { area: 'mortgage-journey', hostname: location.hostname },
						severityLevel: 1
					});

					return router.push('submitted');
				})
				.catch((e) => {
					console.error(e);
					appInsights.trackException({
						exception: e,
						properties: { area: 'mortgage-journey' },
						severityLevel: 4
					});
				})
				.finally(() => {
					this.loading = false;
				});
		}
	},

};
</script>
